import React from 'react';
import {StyleSheet, View} from 'react-native';
import {NavigationContainer, NavigationIndependentTree} from '@react-navigation/native';
import {ThemeProvider} from '@rn-vui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import {rootStore} from '../../../stores/rootStore';
import rewardsTheme from '../../../theme/rewardsTheme';
import {OffersItemWithMflId} from '../../../transport/types';

import ExclusiveOffersTileCompact from './ExclusiveOffersTileCompact';

const mockOffer: OffersItemWithMflId = {
  mflId: 'offer-001',
  offerId: 'LM001',
  heroImage: [
    {
      id: 'hero-1',
      url: 'https://picsum.photos/seed/offer1/600/390',
      filename: 'hero.jpg',
      size: 50000,
      type: 'image',
      width: 600,
      height: 390,
    },
  ],
  heroImageDesktop: [
    {
      id: 'hero-desktop-1',
      url: 'https://picsum.photos/seed/offer1desktop/800/520',
      filename: 'hero-desktop.jpg',
      size: 80000,
      type: 'image',
      width: 800,
      height: 520,
    },
  ],
  tileHeadline: 'Save $10',
  headline: 'Weekend Special Offer',
  description: 'Get $10 off when you spend $50 or more on selected wines.',
  tenant: ['BMT'],
  products: ['wine-001', 'wine-002'],
  barcodes: ['1234567890'],
  status: 'Approved - Live',
  offerTrigger: undefined,
};

const meta = {
  title: 'Components/Rewards/ExclusiveOffersTileCompact',
  component: ExclusiveOffersTileCompact,
  decorators: [
    Story => {
      // Clear activated deals before each story
      rootStore.cartStore.activatedDeals = [];
      return (
        <NavigationIndependentTree>
          <NavigationContainer>
            <ThemeProvider theme={rewardsTheme}>
              <View style={styles.container}>
                <Story />
              </View>
            </ThemeProvider>
          </NavigationContainer>
        </NavigationIndependentTree>
      );
    },
  ],
} satisfies Meta<typeof ExclusiveOffersTileCompact>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Default: Story = {
  args: mockOffer,
};

export const Applied: Story = {
  args: mockOffer,
  decorators: [
    Story => {
      // Set this offer as activated
      rootStore.cartStore.activatedDeals = ['offer-001'];
      return <Story />;
    },
  ],
};

export const LongContent: Story = {
  args: {
    ...mockOffer,
    mflId: 'offer-002',
    tileHeadline: 'Buy 2 Get 1 Free on Premium Wine Collection This Weekend Only',
    headline: 'Premium Wine Collection Special',
    description:
      'Purchase any two bottles of premium wine from our exclusive collection and receive a third bottle absolutely free.',
  },
};

export const ShortContent: Story = {
  args: {
    ...mockOffer,
    mflId: 'offer-003',
    tileHeadline: '$5 Off',
    headline: 'Quick Deal',
    description: 'Save on your next purchase.',
  },
};

export const AllVariants: Story = {
  render: () => {
    // Set one offer as applied for the demo
    rootStore.cartStore.activatedDeals = ['offer-applied'];
    return (
      <View style={styles.variantsContainer}>
        <ExclusiveOffersTileCompact
          {...mockOffer}
          mflId="offer-not-applied"
          tileHeadline="Save $10"
          headline="Weekend Special"
          description="Get $10 off when you spend $50 or more."
        />
        <ExclusiveOffersTileCompact
          {...mockOffer}
          mflId="offer-applied"
          tileHeadline="Buy 2 Get 1 Free"
          headline="Member Exclusive"
          description="On selected craft beers this weekend only."
        />
        <ExclusiveOffersTileCompact
          {...mockOffer}
          mflId="offer-another"
          tileHeadline="20% Off"
          headline="Spirits Sale"
          description="All premium spirits reduced for members."
        />
      </View>
    );
  },
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    justifyContent: 'center',
  },
  variantsContainer: {
    gap: 16,
  },
});
